import { defineComponent as _defineComponent } from 'vue'
import {computed, onMounted, Ref, ref, watch} from "vue";
import { round } from '@/utils/Helpers';
import { RecipeBreakdown } from '@/models/Recipes/RecipeBreakdown';
import { RecipeIngredientBreakdown } from '@/models/Ingredient/RecipeIngredientBreakdown';
import CalculationsRepository from '@/utils/API/CalculationsRepository';
import { Recipe } from '@/models/Recipes/Recipe';
import {LossType} from "@/models/Recipes/LossType";
import VToggleSwitch from "@/components/vToggleSwitch.vue";
import VRecipeBreakdownTable from "@/components/vRecipeBreakdownTable.vue";
import VPreLoader from "@/components/vPreLoader.vue";
import VModal from "@/components/vModal.vue";
import {useTranslations} from "@/hooks/useTranslations";
import {useNotify} from "@/hooks/useNotify";

type vBreakdownReportModalProps = {
  showBreakdownReportModal: boolean;
  parentRecipe: Recipe;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'vBreakdownReportModal',
  props: {
    showBreakdownReportModal: { type: Boolean, default: false },
    parentRecipe: null
  },
  emits: ["on-cancel"],
  setup(__props: any, { emit }: { emit: ({(e: 'on-cancel'): void}), expose: any, slots: any, attrs: any }) {

const props = __props as { showBreakdownReportModal: boolean, parentRecipe: Recipe };



const notifyService = useNotify();
let translations = ref(useTranslations());

function translate(key: string) {
  return translations.value.get(key);
}



// Constants
const countryOptions = computed(() => {
  return [
    {value: '0', name: translate('canada')},
    {value: '1', name: translate('country_us')},
    {value: '2', name: translate('country_uk')},
  ];
});

// Reactive values
const breakdownReportModal: Ref<{
  isOpen: boolean;
  recipe: RecipeBreakdown;
  recipeIngredients: RecipeIngredientBreakdown[];
}> = ref({
  isOpen: false,
  recipe: null,
  recipeIngredients: [],
});

const isLoading = ref(false);
const useRoundedValues = ref(false);
const selectedCountry: Ref<{name: string, value: string}> = ref(countryOptions.value[0]);
const ingredientAllergensByName = ref<Record<string, string>>({});
const recipeLevelAllergensText = ref("--");

watch(
  () => props.showBreakdownReportModal,
  (newValue: boolean) => {
    if (newValue) {
      getRecipeCalculationBreakdown();
      translations.value = useTranslations();
      selectedCountry.value = countryOptions.value[0];
    }
  }
);

onMounted(() => {
  selectedCountry.value = countryOptions.value[0];
});

function closeModal() {
  emit("on-cancel");
}

function onCountryChanged() {
  getRecipeCalculationBreakdown();
}

const tableHeaders = computed(() => {
  const staticHeaders = [
    'Ingredient',
    'Allergens',
    'Quantity',
    'Quantity (G)',
    'Weight %'
  ];

  const firstIngredient = breakdownReportModal.value.recipe?.nutrientFacts?.ingredients?.[0];
  if (!firstIngredient) {
    return staticHeaders;
  }

  const dynamicHeaders = Object.keys(firstIngredient)
    .filter((key) =>
      key !== 'ingredientName' &&
      key !== 'allergenText' &&
      key !== 'weightPercent' &&
      key !== 'quantity' &&
      key !== 'quantityInGrams' &&
      key !== 'quantityInGramsAfterLoss' &&
      key !== 'childRecipeBreakdown' &&
      key !== 'foodClassification'
    );

  // HACK: Added sugar has a naming convention mismatch
  const addedSugarIndex = dynamicHeaders.indexOf('addedSugar');
  if (addedSugarIndex > 0) {
    dynamicHeaders[addedSugarIndex] = 'addedSugar';
  }

  return [...staticHeaders, ...dynamicHeaders];
});

const slicedTableHeaders = computed(() => tableHeaders.value.slice(5));

function normalizeIngredientLookupKey(value: string) {
  return value.trim().toLowerCase();
}

function hydrateIngredientAllergenMapFromBreakdown(recipe: RecipeBreakdown | null) {
  const allergensByName = new Map<string, string>();

  const addIngredients = (ingredients: RecipeIngredientBreakdown[] = []) => {
    ingredients.forEach((ingredient) => {
      if (ingredient?.ingredientName && ingredient?.allergenText) {
        allergensByName.set(normalizeIngredientLookupKey(ingredient.ingredientName), ingredient.allergenText);
      }

      if (ingredient?.childRecipeBreakdown?.nutrientFacts?.ingredients?.length) {
        addIngredients(ingredient.childRecipeBreakdown.nutrientFacts.ingredients);
      }
    });
  };

  addIngredients(recipe?.nutrientFacts?.ingredients || []);

  ingredientAllergensByName.value = Array.from(allergensByName.entries()).reduce<Record<string, string>>((acc, [name, allergenText]) => {
    acc[name] = allergenText;
    return acc;
  }, {});
}

function toggleRoundedDisplay() {
  useRoundedValues.value = !useRoundedValues.value;
}

function getRecipeCalculationBreakdown(){
  let model = {
    recipeId: props.parentRecipe.id,
    country: selectedCountry.value.value,
    useRoundedValues: useRoundedValues.value,
  };
  isLoading.value = true;

  CalculationsRepository.getRecipeCalculationBreakdown(model)
    .then((response) => {
      breakdownReportModal.value.recipe = response.data.result;
      breakdownReportModal.value.recipeIngredients = response.data.result.nutrientFacts.ingredients;
      recipeLevelAllergensText.value = response.data.result.recipeAllergenText || "--";
      hydrateIngredientAllergenMapFromBreakdown(response.data.result);
      isLoading.value = false;
    })
    .catch((error) => {
      if (error.response) {
        notifyService.error(
          error.response.data.errorMessages[0].userVisibleMessage
        );
      }
      else {
        notifyService.error(translate("network-error"));
      }
    });
}

// Download breakdown recipe
const exportRecipeBreakdownToExcelURL = computed(() => {
  let url = `${process.env.VUE_APP_BASE_DOMAIN}/api/processes/recipe/reports/ExportBreakdownPerRecipe?`;

  if(breakdownReportModal.value.recipe){
    const queryParams = `country=${selectedCountry.value.value}&recipeId=${props.parentRecipe.id}&useRoundedValues=${useRoundedValues.value}`;
    url += queryParams;
  }

  return url;
});

return { __sfc: true,props, notifyService, translations, translate, emit, countryOptions, breakdownReportModal, isLoading, useRoundedValues, selectedCountry, ingredientAllergensByName, recipeLevelAllergensText, closeModal, onCountryChanged, tableHeaders, slicedTableHeaders, normalizeIngredientLookupKey, hydrateIngredientAllergenMapFromBreakdown, toggleRoundedDisplay, getRecipeCalculationBreakdown, exportRecipeBreakdownToExcelURL, round, LossType, VToggleSwitch, VRecipeBreakdownTable, VPreLoader, VModal }
}

})